import type { LaunchNetworkConfig } from "@models/launchNetwork.model";

type Props = {
  network: string;
  label: string;
  color: string;
  icon: string;
  config: LaunchNetworkConfig;
  resourceName: string | null;
  templateName: string | null;
  isBusy: boolean;
  onPickResource: () => void;
  onPickTemplate: () => void;
  onInspectLinks: () => void;
  onToggle: (nextEnabled: boolean) => void;
};

export default function PublisherRow({
  label,
  color,
  icon,
  config,
  resourceName,
  templateName,
  isBusy,
  onPickResource,
  onPickTemplate,
  onInspectLinks,
  onToggle,
}: Props) {
  const hasResource = !!config.network_resource_id;
  const hasTemplate = !!config.network_template_id;
  const isConfigured = hasResource && hasTemplate;

  const pill = config.is_enabled && isConfigured
    ? { className: "campaign-pill campaign-pill--live", text: "Live" }
    : isConfigured
      ? { className: "campaign-pill campaign-pill--muted", text: "Paused" }
      : { className: "campaign-pill campaign-pill--warn", text: "Needs setup" };

  return (
    <div className="publisher-row">
      <div className="publisher-row__identity">
        <div className="publisher-row__logo" style={{ backgroundColor: color, color: "#fff" }}>
          <i className={icon} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="publisher-row__name">{label}</div>
          <span className={pill.className}>{pill.text}</span>
        </div>
      </div>

      <div className="publisher-row__fields">
        <button
          type="button"
          className={`publisher-field ${hasResource ? "" : "publisher-field--empty"}`}
          onClick={onPickResource}
          disabled={isBusy}
        >
          <div className="publisher-field__key">Resource</div>
          <div className="publisher-field__value">
            {resourceName ?? (hasResource ? "Loading..." : "Select a resource")}
          </div>
        </button>

        <button
          type="button"
          className={`publisher-field ${hasResource && !hasTemplate ? "publisher-field--empty" : ""}`}
          onClick={onPickTemplate}
          // Templates are scoped to a resource, so there is nothing to choose yet.
          disabled={isBusy || !hasResource}
          title={hasResource ? undefined : "Select an ad resource first"}
        >
          <div className="publisher-field__key">Template</div>
          <div className="publisher-field__value">
            {hasResource
              ? (templateName ?? (hasTemplate ? "Loading..." : "Select a template"))
              : "Pick a resource first"}
          </div>
        </button>
      </div>

      <div className="publisher-row__actions">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          style={{ fontSize: "0.72rem" }}
          onClick={onInspectLinks}
          disabled={isBusy || !isConfigured}
          title={isConfigured ? undefined : "Configure this publisher to inspect its links"}
        >
          Links
        </button>
        <button
          type="button"
          className={`publisher-toggle ${config.is_enabled ? "publisher-toggle--on" : ""}`}
          onClick={() => onToggle(!config.is_enabled)}
          disabled={isBusy || (!config.is_enabled && !isConfigured)}
          aria-label={config.is_enabled ? `Turn ${label} off` : `Turn ${label} on`}
          title={
            isConfigured
              ? config.is_enabled ? `Turn ${label} off` : `Turn ${label} on`
              : "Select a resource and a template to enable"
          }
        >
          <span className="publisher-toggle__knob" />
        </button>
      </div>
    </div>
  );
}
